// Card Rules Engine for Multiplayer
// This handles all card requirements and special effects

import { availableCards } from '../CardData';

// Generate unique ID for cards
const generateUniqueCardId = (card, prefix = '') => {
  return `${prefix}${card.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Check if a card can be played based on prerequisites
// playedCards: cards in player's played area
// centerCards: cards in the shared center area (from opponent's discard) - count for both players
export const checkPrerequisites = (cardToPlay, playedCards, centerCards = []) => {
  // Combine player's cards AND center cards for prerequisite checking
  const allAvailableCards = [...playedCards, ...centerCards];
  const hasCard = cardName => allAvailableCards.some(card => card.name === cardName);

  switch (cardToPlay.type) {
    case 'Eco':
      switch (cardToPlay.name) {
        case 'Sapling':
          return hasCard('Seed');
        case 'Tree':
          return hasCard('Sapling') || (hasCard('Seed') && hasCard('Patience'));
        case 'Forest':
          return hasCard('Tree');
        case 'Garden':
          return (hasCard('Naturalist') && hasCard('Seed')) || (hasCard('Seed') && hasCard('Patience'));
        case 'Habitat':
          return hasCard('Naturalist') || hasCard('Forest');
        case 'Oasis':
          return (hasCard('Explorer') && hasCard('Patience')) || 
                 (hasCard('Explorer') && hasCard('Naturalist'));
        case 'Thriving Ecosystem':
          return hasCard('Forest') && hasCard('Love');
        case 'Bloom':
          return hasCard('Tree');
        case 'Farm':
          return hasCard('Community');
        case 'Uncharted':
          return hasCard('Explorer');
        default:
          return true;
      }

    case 'Tech':
      switch (cardToPlay.name) {
        case 'Battery':
          return hasCard('Tools') && hasCard('Materials');
        case 'Solar Panel':
        case 'Wind Turbine':
          return hasCard('Battery') && hasCard('Engineer');
        case 'Nuclear Energy':
          return hasCard('Battery') && hasCard('Engineer') && hasCard('Physicist');
        case 'Settlement':
          return hasCard('Solar Panel') || hasCard('Wind Turbine') || 
               hasCard('Nuclear Energy') || 
                  hasCard('Farm');
        case 'City':
          const hasFactory = hasCard('Factory');
          const hasCommunity = hasCard('Community');
          const hasSettlement = hasCard('Settlement');
                 
          const energySources = new Set();
          if (hasCard('Solar Panel')) energySources.add('Solar Panel');
          if (hasCard('Wind Turbine')) energySources.add('Wind Turbine');
          if (hasCard('Nuclear Energy')) energySources.add('Nuclear Energy');
                 
          return hasFactory && (hasCommunity || hasSettlement) && energySources.size >= 2;
        case 'Thriving Civilization':
          return hasCard('City') && hasCard('Love');
        case 'Factory':
          return hasCard('Settlement');
        case 'Recycle':
          const hasScavenger = hasCard('Scavenger');
          const hasEnergy = hasCard('Solar Panel') || 
                         hasCard('Wind Turbine') || 
                         hasCard('Nuclear Energy');
          return hasScavenger && hasEnergy;
        default:
          return true;
      }

    case 'Wisdom':
      switch (cardToPlay.name) {
        case 'Patience':
          return hasCard('Meditation');
        case 'Love':
          return hasCard('Patience');
        case 'Explorer':
        case 'Physicist':
          return hasCard('Curiosity');
        case 'Thriving Spirit':
          return hasCard('Curiosity') && hasCard('Love');
        case 'Community':
          return hasCard('Garden') || hasCard('Love');
        default:
          return true;
      }

    default:
      return false;
  }
};

// Handle special card effects and return the updated game state
export const handleSpecialCardEffects = (cardName, playerState) => {
  const effects = {
    spaceModifier: 0,
    drawCards: 0,
    cardsToAddToDeck: [],
    requiresModal: null // For cards that need player interaction
  };

  switch (cardName) {
    case 'Observation':
      effects.drawCards = 1;
      break;
    case 'Explorer':
      effects.drawCards = 2;
      break;
    case 'Library':
      effects.requiresModal = 'library';
      break;
    case 'Community':
      effects.spaceModifier = 2;
      effects.drawCards = 1;
      break;
    case 'Settlement':
      effects.spaceModifier = 5;
      break;
    case 'City':
      effects.spaceModifier = 5;
      break;
    case 'Thriving Civilization':
      effects.spaceModifier = 10;
      const factoryCard = availableCards.find(card => card.name === 'Factory');
      const cityCard = availableCards.find(card => card.name === 'City');
      if (factoryCard && cityCard) {
        effects.cardsToAddToDeck = [
          { ...factoryCard, id: generateUniqueCardId(factoryCard, 'deck-') },
          { ...cityCard, id: generateUniqueCardId(cityCard, 'deck-') }
        ];
      }
      break;
    case 'Factory':
      effects.requiresModal = 'factory';
      break;
    case 'Bloom':
      const bloomCards = ['Sapling', 'Tree', 'Forest', 'Wildflowers']
        .map(name => {
          const card = availableCards.find(card => card.name === name);
          if (card) {
            return { ...card, id: generateUniqueCardId(card, 'deck-') };
          }
          return null;
        })
        .filter(card => card !== null);
      effects.cardsToAddToDeck = bloomCards;
      break;
    case 'Thriving Ecosystem':
      const bloomCard = availableCards.find(card => card.name === 'Bloom');
      if (bloomCard) {
        effects.cardsToAddToDeck = [
          { ...bloomCard, id: generateUniqueCardId(bloomCard, 'deck-') }
        ];
      }
      break;
    case 'Uncharted':
      effects.spaceModifier = 2;
      // Generate 2 random cards (excluding Materials)
      const validCards = availableCards.filter(card => card.name !== 'Materials');
      const randomCard1 = validCards[Math.floor(Math.random() * validCards.length)];
      const randomCard2 = validCards[Math.floor(Math.random() * validCards.length)];
      effects.cardsToAddToDeck = [
        { ...randomCard1, id: generateUniqueCardId(randomCard1, 'deck-') },
        { ...randomCard2, id: generateUniqueCardId(randomCard2, 'deck-') }
      ];
      break;
    case 'Scavenger':
      effects.requiresModal = 'scavenger';
      break;
    case 'Recycle':
      effects.requiresModal = 'recycle';
      break;
    case 'Futurist':
      effects.requiresModal = 'futurist';
      break;
    case 'Oasis':
      effects.spaceModifier = 3;
      break;
    case 'Habitat':
      effects.spaceModifier = 2;
      break;
    case 'Love':
      effects.spaceModifier = 1;
      break;
    case 'Thriving Spirit':
      effects.spaceModifier = 1;
      effects.requiresModal = 'thrivingSpirit';
      break;
    default:
      // No special effects
      break;
  }

  return effects;
};

// Shuffle array (deck)
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const CardRulesEngine = {
  checkPrerequisites,
  handleSpecialCardEffects,
  shuffleArray
};

export default CardRulesEngine;



